/**
 * useWhatsNew — tracks which release notes the current user has seen.
 *
 * Two independent per-user flags live in localStorage:
 *   • last-seen   → the newest release id the user has actually looked at.
 *                   Drives the "New" dot on the gift bell.
 *   • auto-shown  → the newest release id we've already auto-popped for them.
 *                   Stops the popup re-appearing every page load.
 *
 * Both keys are scoped to `user_id` (falling back to `tech_id`) so a shared
 * device shows the update once per person, mirroring the admin-tile badge.
 *
 * All consumers stay in sync via a window CustomEvent — clicking one bell (or
 * the auto-popup, or the full page) clears the badge on every other bell.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  WHATS_NEW_RELEASES,
  LATEST_WHATS_NEW_ID,
  type WhatsNewRelease,
} from '@/data/whatsNew';

const UPDATED_EVENT = 'whats-new-updated';

function currentUserId(): string {
  if (typeof window === 'undefined') return 'anon';
  try {
    return (
      localStorage.getItem('user_id') ||
      localStorage.getItem('tech_id') ||
      'anon'
    );
  } catch {
    return 'anon';
  }
}

const seenKey = (uid: string) => `whats_new_last_seen.${uid}`;
const autoShownKey = (uid: string) => `whats_new_autoshown.${uid}`;

function readKey(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, value);
    window.dispatchEvent(new CustomEvent(UPDATED_EVENT));
  } catch {
    // Storage blocked — nothing to do; the user just sees the badge again.
  }
}

export interface UseWhatsNew {
  /** All releases, newest first. */
  releases: WhatsNewRelease[];
  /** Id of the newest release. */
  latestId: string;
  /** True when there's a newest release the user hasn't opened yet. */
  hasUnseen: boolean;
  /** How many releases are newer than the one the user last saw (capped display handled by UI). */
  unseenCount: number;
  /** True when we should automatically pop the update for this user (once per release). */
  shouldAutoOpen: boolean;
  /** Record that the auto-popup has been shown — without clearing the badge. */
  markAutoShown: () => void;
  /** Mark everything seen: clears the badge AND suppresses the auto-popup. */
  markViewed: () => void;
}

export function useWhatsNew(): UseWhatsNew {
  const [uid] = useState(currentUserId);
  const [lastSeen, setLastSeen] = useState<string | null>(() => readKey(seenKey(uid)));
  const [autoShown, setAutoShown] = useState<string | null>(() => readKey(autoShownKey(uid)));

  // Keep every mounted consumer in sync (multiple bells, popup, page).
  useEffect(() => {
    const refresh = () => {
      setLastSeen(readKey(seenKey(uid)));
      setAutoShown(readKey(autoShownKey(uid)));
    };
    window.addEventListener(UPDATED_EVENT, refresh);
    window.addEventListener('storage', refresh); // other tabs
    return () => {
      window.removeEventListener(UPDATED_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [uid]);

  const hasUnseen = LATEST_WHATS_NEW_ID !== '' && lastSeen !== LATEST_WHATS_NEW_ID;

  // Count how many releases are newer than the last one the user saw. If they've
  // never looked (null / unknown id), everything counts as unseen.
  let unseenCount = 0;
  if (hasUnseen) {
    const idx = WHATS_NEW_RELEASES.findIndex((r) => r.id === lastSeen);
    unseenCount = idx === -1 ? WHATS_NEW_RELEASES.length : idx;
  }

  const shouldAutoOpen =
    LATEST_WHATS_NEW_ID !== '' && autoShown !== LATEST_WHATS_NEW_ID;

  const markAutoShown = useCallback(() => {
    if (!LATEST_WHATS_NEW_ID) return;
    writeKey(autoShownKey(uid), LATEST_WHATS_NEW_ID);
    setAutoShown(LATEST_WHATS_NEW_ID);
  }, [uid]);

  const markViewed = useCallback(() => {
    if (!LATEST_WHATS_NEW_ID) return;
    writeKey(seenKey(uid), LATEST_WHATS_NEW_ID);
    writeKey(autoShownKey(uid), LATEST_WHATS_NEW_ID);
    setLastSeen(LATEST_WHATS_NEW_ID);
    setAutoShown(LATEST_WHATS_NEW_ID);
  }, [uid]);

  return {
    releases: WHATS_NEW_RELEASES,
    latestId: LATEST_WHATS_NEW_ID,
    hasUnseen,
    unseenCount,
    shouldAutoOpen,
    markAutoShown,
    markViewed,
  };
}
